import React, { useMemo, useState } from "react";
import {
  Modal,
  Stack,
  Title,
  Text,
  Table,
  Badge,
  Group,
  Switch,
  SegmentedControl,
  Loader,
  Center,
  ScrollArea,
  Tooltip,
  Alert,
  Tabs,
  Button,
  TextInput,
  NumberInput,
  ActionIcon,
  Paper,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconUsers,
  IconBuilding,
  IconBriefcase,
  IconAlertCircle,
  IconCalendar,
  IconFileText,
  IconInfoCircle,
  IconLock,
  IconEdit,
  IconTrash,
  IconPlus,
  IconCheck,
  IconX,
} from "@tabler/icons-react";
import { useAuth } from "../hooks/useAuth";
import { useIsMobile } from "../hooks/useIsMobile";
import {
  useRolePermissions,
  useUpdateRolePermission,
  type PermissionRole,
} from "../hooks/useRolePermissions";
import {
  useAdminRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  type AdminRole,
} from "../hooks/useAdminRoles";
import { modals } from "@mantine/modals";
import {
  buildResourceRows,
  actionVerbOf,
  humanizeAction,
  isResourceScopedKey,
  isActionKey,
  type ResourceRow,
} from "../lib/resourcePermissions";

interface RolePermissionsModalProps {
  opened: boolean;
  onClose: () => void;
}

// Labels and categories come from the API (PermissionRegistry on the backend).
// No static map needed — adding a new Permission class auto-appears here.

// Category → icon mapping for display. Any category string from the registry
// not in this map falls back to IconShieldCheck.
const CATEGORY_ICONS: Record<string, React.FC<{ size?: number }>> = {
  Residents: IconUsers,
  Incidents: IconAlertCircle,
  "Staff & Schedules": IconCalendar,
  Operations: IconBriefcase,
  "Reporting & Admin": IconFileText,
  "User Management": IconBuilding,
  "Platform & Facilities": IconShieldCheck,
};

const ROLE_COLORS: Record<string, string> = {
  "role:platform:admin": "red",
  "role:platform:manager": "pink",
  "role:org:admin": "orange",
  "role:facility:manager": "blue",
  "role:staff:medical": "teal",
  "role:staff:care": "green",
  "role:audit:admin": "grape",
  "role:audit:external": "violet",
};

// ─── System-role matrix (read-only, built-in roles) ──────────────────────────
//
// Built-in roles (role_key !== null) are policy-as-code: defined in code and
// reconciled on each server boot. This is the original read-only matrix, now
// scoped to system roles only — custom roles are edited in the resource-grouped
// editor below.

function SystemRoleMatrix({
  roles,
  matrix,
  permissions,
}: {
  roles: PermissionRole[];
  matrix: Record<string, Record<string, boolean>>;
  permissions: { key: string; label: string; category: string }[];
}) {
  const getValue = (id: string, perm: string) => matrix[id]?.[perm] ?? false;
  const colCount = roles.length + 1; // permission label + one per role

  // Group permissions by category using data from the API registry.
  // Any new Permission added on the backend automatically appears here.
  const grouped = permissions.reduce<
    Map<string, Array<{ key: string; label: string }>>
  >((acc, p) => {
    const list = acc.get(p.category) ?? [];
    list.push({ key: p.key, label: p.label });
    acc.set(p.category, list);
    return acc;
  }, new Map());

  return (
    <Stack gap="md">
      <Alert
        icon={<IconLock size={16} />}
        color="gray"
        variant="light"
        radius="md"
      >
        Built-in roles are managed in code and shown read-only. To customize
        permissions, switch to <strong>Custom roles</strong> above.
      </Alert>

      {/* Single table — one header, category rows as visual dividers */}
      {/* h is required so ScrollArea creates its own scroll context, enabling position:sticky on thead */}
      <ScrollArea type="hover" h="calc(70vh - 200px)">
        {/* eslint-disable-next-line @livingsyncs/contained-table -- US-186 legacy, see follow-up */}
        <Table
          striped
          withTableBorder
          withColumnBorders
          highlightOnHover
          style={{ minWidth: Math.max(440, roles.length * 68 + 220) }}
        >
          {/* ── Single sticky header ── */}
          <Table.Thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <Table.Tr style={{ verticalAlign: "bottom" }}>
              <Table.Th
                style={{
                  minWidth: 220,
                  width: 220,
                  verticalAlign: "bottom",
                  paddingBottom: 8,
                  background: "var(--mantine-color-default)",
                }}
              >
                Permission
              </Table.Th>
              {roles.map((role) => (
                <Table.Th
                  key={role.id}
                  ta="center"
                  style={{
                    width: 90,
                    minWidth: 90,
                    background: "var(--mantine-color-default)",
                    verticalAlign: "bottom",
                    padding: "8px 6px",
                  }}
                >
                  <Tooltip
                    label={role.role_key ?? "custom role"}
                    withArrow
                    position="top"
                  >
                    <div>
                      {role.role_key && (
                        <IconLock
                          size={10}
                          style={{
                            opacity: 0.4,
                            display: "block",
                            margin: "0 auto 2px",
                          }}
                        />
                      )}
                      <Text
                        size="xs"
                        fw={600}
                        c={
                          role.role_key
                            ? (ROLE_COLORS[role.role_key] ?? "gray")
                            : "gray"
                        }
                        style={{ lineHeight: 1.3, wordBreak: "break-word" }}
                      >
                        {role.name}
                      </Text>
                    </div>
                  </Tooltip>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {[...grouped.entries()].map(([categoryLabel, perms]) => {
              const CategoryIcon =
                CATEGORY_ICONS[categoryLabel] ?? IconShieldCheck;
              return (
                <React.Fragment key={categoryLabel}>
                  {/* Category separator row — uses default-border which adapts to dark mode */}
                  <Table.Tr key={`cat-${categoryLabel}`}>
                    <Table.Td
                      colSpan={colCount}
                      py={6}
                      px="sm"
                      style={{
                        background: "var(--mantine-color-default-hover)",
                        borderTop:
                          "2px solid var(--mantine-color-default-border)",
                      }}
                    >
                      <Group gap={6}>
                        <CategoryIcon size={14} />
                        <Text
                          size="xs"
                          fw={700}
                          tt="uppercase"
                          c="dimmed"
                          style={{ letterSpacing: "0.05em" }}
                        >
                          {categoryLabel}
                        </Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>

                  {/* Permission rows — striping handled by Table striped prop */}
                  {perms.map((p) => (
                    <Table.Tr key={p.key}>
                      <Table.Td pl="lg">
                        <Group gap={6} wrap="nowrap">
                          <Text size="sm">{p.label}</Text>
                          {isActionKey(p.key) && (
                            <Tooltip
                              label="Elevated action — granted on its own, not implied by Write"
                              withArrow
                              position="top"
                            >
                              <Badge
                                color="orange"
                                variant="light"
                                size="xs"
                                tt="none"
                                style={{ cursor: "help" }}
                              >
                                action
                              </Badge>
                            </Tooltip>
                          )}
                        </Group>
                      </Table.Td>
                      {roles.map((role) => {
                        const value = getValue(role.id, p.key);
                        return (
                          <Table.Td key={role.id} ta="center" p={4}>
                            <Text
                              size="sm"
                              c={value ? "green" : "gray"}
                              fw={value ? 700 : 400}
                              ta="center"
                            >
                              {value ? "✓" : "—"}
                            </Text>
                          </Table.Td>
                        );
                      })}
                    </Table.Tr>
                  ))}
                </React.Fragment>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <Text size="xs" c="dimmed">
        <strong>Note:</strong> Built-in role permissions are defined in code and
        reconciled on each server restart.
      </Text>
    </Stack>
  );
}

// ─── Custom-role permission editor (resource-grouped, typed vocabulary) ───────
//
// AC-G3a: custom roles (role_key === null) may only hold the new resource-scoped
// vocabulary (104 keys). The editor is grouped by resource; each row exposes
// Read / Write toggles (only when the catalog has that key) plus any elevated
// action toggles for the resource. Legacy keys a role still holds are shown
// read-only with a "legacy — migrating" note and are never newly grantable.

const RESOURCE_ROWS: ResourceRow[] = buildResourceRows();

function CustomRolePermissionsEditor({
  role,
  grants,
  legacyHeld,
  isAdmin,
}: {
  role: PermissionRole;
  /** permission_key → allowed, for THIS role only. */
  grants: Record<string, boolean>;
  /** Legacy keys (not in the new vocabulary) this role currently holds. */
  legacyHeld: { key: string; label: string }[];
  isAdmin: boolean;
}) {
  const update = useUpdateRolePermission();
  const has = (key: string) => grants[key] === true;

  const toggle = (key: string, allowed: boolean) => {
    update.mutate({ role_id: role.id, permission_key: key, allowed });
  };

  // Implication-aware UX: granting Write implies Read. We still store both
  // explicitly — when Write is turned on, also ensure Read is granted.
  const onWriteChange = (
    readKey: string | null,
    writeKey: string,
    on: boolean,
  ) => {
    toggle(writeKey, on);
    if (on && readKey && !has(readKey)) {
      toggle(readKey, true);
    }
  };

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Group gap="xs">
          <Badge color="gray" variant="light" size="sm" tt="none">
            {role.name}
          </Badge>
          <Text size="xs" c="dimmed">
            Custom role — grant from the resource-scoped vocabulary below.
          </Text>
        </Group>
      </Group>

      {!isAdmin && (
        <Alert
          icon={<IconLock size={16} />}
          color="gray"
          variant="light"
          radius="md"
        >
          Read-only view — only Platform Admins can modify permissions.
        </Alert>
      )}

      {legacyHeld.length > 0 && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="yellow"
          variant="light"
          radius="md"
          title="Legacy permissions — migrating"
        >
          <Text size="xs">
            This role still holds {legacyHeld.length} legacy permission
            {legacyHeld.length === 1 ? "" : "s"} that{" "}
            {legacyHeld.length === 1 ? "is" : "are"} being retired. They cannot
            be re-granted here:
          </Text>
          <Group gap={6} mt={6}>
            {legacyHeld.map((p) => (
              <Badge
                key={p.key}
                color="gray"
                variant="outline"
                size="xs"
                tt="none"
                style={{ opacity: 0.7 }}
              >
                {p.label}
              </Badge>
            ))}
          </Group>
        </Alert>
      )}

      <ScrollArea type="hover" h="calc(70vh - 240px)">
        {/* eslint-disable-next-line @livingsyncs/contained-table -- US-186 legacy, see follow-up */}
        <Table
          withTableBorder
          withColumnBorders
          highlightOnHover
          style={{ minWidth: 560 }}
        >
          <Table.Thead style={{ position: "sticky", top: 0, zIndex: 2 }}>
            <Table.Tr>
              <Table.Th
                style={{
                  minWidth: 220,
                  background: "var(--mantine-color-default)",
                }}
              >
                Resource
              </Table.Th>
              <Table.Th
                ta="center"
                style={{
                  width: 90,
                  background: "var(--mantine-color-default)",
                }}
              >
                Read
              </Table.Th>
              <Table.Th
                ta="center"
                style={{
                  width: 90,
                  background: "var(--mantine-color-default)",
                }}
              >
                Write
              </Table.Th>
              <Table.Th
                style={{
                  minWidth: 160,
                  background: "var(--mantine-color-default)",
                }}
              >
                Actions
              </Table.Th>
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {RESOURCE_ROWS.map((row) => {
              const writeOn = row.writeKey ? has(row.writeKey) : false;
              const readGranted = row.readKey ? has(row.readKey) : false;
              // Read is logically covered when Write is on (write→read implication).
              const readCovered = readGranted || writeOn;
              const disabled = !isAdmin || update.isPending;
              return (
                <Table.Tr key={row.resource}>
                  <Table.Td>
                    <Text size="sm">{row.label}</Text>
                    <Text size="xs" c="dimmed">
                      {row.resource}
                    </Text>
                  </Table.Td>

                  {/* Read */}
                  <Table.Td ta="center" p={4}>
                    {row.readKey ? (
                      <Tooltip
                        label={
                          writeOn && !readGranted
                            ? "Implied by Write — will be stored explicitly"
                            : row.readKey
                        }
                        withArrow
                        position="top"
                      >
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Switch
                            size="xs"
                            aria-label={`${row.label}: Read`}
                            checked={readCovered}
                            disabled={disabled || (writeOn && !readGranted)}
                            onChange={(e) =>
                              toggle(row.readKey!, e.currentTarget.checked)
                            }
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <Text size="sm" c="dimmed" ta="center">
                        —
                      </Text>
                    )}
                  </Table.Td>

                  {/* Write */}
                  <Table.Td ta="center" p={4}>
                    {row.writeKey ? (
                      <Tooltip label={row.writeKey} withArrow position="top">
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <Switch
                            size="xs"
                            aria-label={`${row.label}: Write`}
                            checked={writeOn}
                            disabled={disabled}
                            onChange={(e) =>
                              onWriteChange(
                                row.readKey,
                                row.writeKey!,
                                e.currentTarget.checked,
                              )
                            }
                          />
                        </div>
                      </Tooltip>
                    ) : (
                      <Text size="sm" c="dimmed" ta="center">
                        —
                      </Text>
                    )}
                  </Table.Td>

                  {/* Elevated actions */}
                  <Table.Td>
                    {row.actionKeys.length === 0 ? (
                      <Text size="xs" c="dimmed">
                        —
                      </Text>
                    ) : (
                      <Stack gap={4}>
                        {row.actionKeys.map((actionKey) => (
                          <Tooltip
                            key={actionKey}
                            label={actionKey}
                            withArrow
                            position="top"
                          >
                            <div>
                              <Switch
                                size="xs"
                                aria-label={`${row.label}: ${humanizeAction(
                                  actionVerbOf(actionKey),
                                )}`}
                                label={humanizeAction(actionVerbOf(actionKey))}
                                checked={has(actionKey)}
                                disabled={disabled}
                                onChange={(e) =>
                                  toggle(actionKey, e.currentTarget.checked)
                                }
                              />
                            </div>
                          </Tooltip>
                        ))}
                      </Stack>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollArea>

      <Text size="xs" c="dimmed">
        <strong>Note:</strong> Granting <em>Write</em> implies <em>Read</em>;
        both are stored explicitly. <em>Actions</em> (e.g. Close, Administer)
        are separate elevated grants — <em>Write</em> does not include them.
        Changes take effect on the user's next login.
      </Text>
    </Stack>
  );
}

// ─── Permissions tab ──────────────────────────────────────────────────────────

function PermissionsTab({
  isAdmin,
  opened,
}: {
  isAdmin: boolean;
  opened: boolean;
}) {
  const { data, isLoading } = useRolePermissions(opened);
  const [view, setView] = useState<"system" | "custom">("system");
  const [selectedCustomId, setSelectedCustomId] = useState<string | null>(null);

  const roles = useMemo(() => data?.roles ?? [], [data?.roles]);
  const systemRoles = useMemo(
    () => roles.filter((r) => r.role_key !== null),
    [roles],
  );
  const customRoles = useMemo(
    () => roles.filter((r) => r.role_key === null),
    [roles],
  );

  // Legacy keys this build knows about = catalog permissions whose key is NOT in
  // the new resource-scoped vocabulary. Held legacy keys are shown read-only.
  const legacyByRole = useMemo(() => {
    const labelByKey = new Map(
      (data?.permissions ?? []).map((p) => [p.key, p.label]),
    );
    const result: Record<string, { key: string; label: string }[]> = {};
    for (const role of customRoles) {
      const grants = data?.matrix[role.id] ?? {};
      result[role.id] = Object.entries(grants)
        .filter(([key, allowed]) => allowed && !isResourceScopedKey(key))
        .map(([key]) => ({ key, label: labelByKey.get(key) ?? key }));
    }
    return result;
  }, [customRoles, data]);

  const selectedCustom =
    customRoles.find((r) => r.id === selectedCustomId) ??
    customRoles[0] ??
    null;

  if (isLoading) {
    return (
      <Center h={200}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <SegmentedControl
          value={view}
          onChange={(v) => setView(v as "system" | "custom")}
          data={[
            { label: "Built-in roles", value: "system" },
            { label: "Custom roles", value: "custom" },
          ]}
          size="xs"
        />
        {view === "custom" && customRoles.length > 0 && (
          <SegmentedControl
            value={selectedCustom?.id ?? ""}
            onChange={setSelectedCustomId}
            data={customRoles.map((r) => ({ label: r.name, value: r.id }))}
            size="xs"
          />
        )}
      </Group>

      {view === "system" ? (
        <SystemRoleMatrix
          roles={systemRoles}
          matrix={data?.matrix ?? {}}
          permissions={data?.permissions ?? []}
        />
      ) : customRoles.length === 0 ? (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="blue"
          variant="light"
          radius="md"
        >
          No custom roles yet. Create one in the <strong>Roles</strong> tab,
          then return here to grant permissions.
        </Alert>
      ) : selectedCustom ? (
        <CustomRolePermissionsEditor
          key={selectedCustom.id}
          role={selectedCustom}
          grants={data?.matrix[selectedCustom.id] ?? {}}
          legacyHeld={legacyByRole[selectedCustom.id] ?? []}
          isAdmin={isAdmin}
        />
      ) : null}
    </Stack>
  );
}

// ─── Role row (inline edit) ───────────────────────────────────────────────────

function RoleRow({ role, isAdmin }: { role: AdminRole; isAdmin: boolean }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(role.name);
  const [description, setDescription] = useState(role.description ?? "");
  const updateRole = useUpdateRole();
  const deleteRole = useDeleteRole();
  const isBuiltIn = role.role_key !== null;
  const isPlatformAdminRole = role.role_key === "role:platform:admin";

  const save = () => {
    if (!name.trim()) return;
    updateRole.mutate(
      { id: role.id, name: name.trim(), description: description || null },
      { onSuccess: () => setEditing(false) },
    );
  };

  const cancel = () => {
    setName(role.name);
    setDescription(role.description ?? "");
    setEditing(false);
  };

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: "Delete role",
      children: (
        <Text size="sm">
          Delete <strong>{role.name}</strong>? Any users assigned this role will
          lose it immediately.
        </Text>
      ),
      labels: { confirm: "Delete", cancel: "Cancel" },
      confirmProps: { color: "red" },
      onConfirm: () => deleteRole.mutate(role.id),
    });

  return (
    <Paper withBorder p="sm" radius="md">
      {editing ? (
        <Stack gap="xs">
          <Group gap="xs" align="flex-end">
            <TextInput
              label="Name"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              size="xs"
              style={{ flex: 1 }}
            />
            <TextInput
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              size="xs"
              style={{ flex: 2 }}
              placeholder="Optional"
            />
            <ActionIcon
              color="green"
              variant="light"
              onClick={save}
              loading={updateRole.isPending}
            >
              <IconCheck size={14} />
            </ActionIcon>
            <ActionIcon color="gray" variant="light" onClick={cancel}>
              <IconX size={14} />
            </ActionIcon>
          </Group>
        </Stack>
      ) : (
        <Group justify="space-between">
          <Group gap="xs">
            <Badge
              color={ROLE_COLORS[role.role_key ?? ""] ?? "gray"}
              variant="light"
              size="sm"
              tt="none"
            >
              {role.name}
            </Badge>
            {isBuiltIn && (
              <Tooltip label={`System key: ${role.role_key}`} withArrow>
                <IconLock size={12} color="gray" style={{ cursor: "help" }} />
              </Tooltip>
            )}
            {role.description && (
              <Text size="xs" c="dimmed">
                {role.description}
              </Text>
            )}
          </Group>

          {isAdmin && (
            <Group gap={4}>
              <ActionIcon
                size="sm"
                variant="subtle"
                disabled={isPlatformAdminRole}
                title={
                  isPlatformAdminRole
                    ? "Platform Admin permissions cannot be edited"
                    : "Edit"
                }
                onClick={() => setEditing(true)}
              >
                <IconEdit size={13} />
              </ActionIcon>
              {!isBuiltIn && (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  color="red"
                  onClick={confirmDelete}
                  loading={deleteRole.isPending}
                >
                  <IconTrash size={13} />
                </ActionIcon>
              )}
            </Group>
          )}
        </Group>
      )}
    </Paper>
  );
}

// ─── Roles management tab ─────────────────────────────────────────────────────

function RolesTab({ isAdmin, opened }: { isAdmin: boolean; opened: boolean }) {
  const { data: roles, isLoading } = useAdminRoles(opened);
  const createRole = useCreateRole();
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newLevel, setNewLevel] = useState<number>(50);

  const submitCreate = () => {
    if (!newName.trim()) return;
    createRole.mutate(
      {
        name: newName.trim(),
        description: newDesc || undefined,
        level: newLevel,
      },
      {
        onSuccess: () => {
          setNewName("");
          setNewDesc("");
          setNewLevel(50);
          setShowCreate(false);
        },
      },
    );
  };

  if (isLoading) {
    return (
      <Center h={200}>
        <Loader size="lg" />
      </Center>
    );
  }

  const builtIn = (roles ?? []).filter((r) => r.role_key !== null);
  const custom = (roles ?? []).filter((r) => r.role_key === null);

  return (
    <Stack gap="lg">
      {isAdmin && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="blue"
          variant="light"
          radius="md"
        >
          Built-in roles have a system key (🔒) — their name can be changed but
          they cannot be deleted. Custom roles are purely permission-driven.
        </Alert>
      )}

      {/* Built-in roles */}
      <div>
        <Text fw={600} size="sm" mb="xs">
          Built-in Roles
        </Text>
        <Stack gap="xs">
          {builtIn.map((role) => (
            <RoleRow key={role.id} role={role} isAdmin={isAdmin} />
          ))}
        </Stack>
      </div>

      {/* Custom roles */}
      <div>
        <Group justify="space-between" mb="xs">
          <Text fw={600} size="sm">
            Custom Roles
          </Text>
          {isAdmin && (
            <Button
              size="xs"
              leftSection={<IconPlus size={13} />}
              variant="light"
              onClick={() => setShowCreate((v) => !v)}
            >
              New Role
            </Button>
          )}
        </Group>

        {/* Create form */}
        {showCreate && (
          <Paper withBorder p="sm" radius="md" mb="xs">
            <Stack gap="xs">
              <Group gap="xs" align="flex-end">
                <TextInput
                  label="Name"
                  value={newName}
                  onChange={(e) => setNewName(e.currentTarget.value)}
                  size="xs"
                  style={{ flex: 1 }}
                  placeholder="e.g. Senior Care Staff"
                />
                <TextInput
                  label="Description"
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.currentTarget.value)}
                  size="xs"
                  style={{ flex: 2 }}
                  placeholder="Optional"
                />
                <NumberInput
                  label="Level"
                  value={newLevel}
                  onChange={(v) => setNewLevel(Number(v))}
                  size="xs"
                  w={80}
                  min={1}
                  max={999}
                />
                <ActionIcon
                  color="green"
                  variant="light"
                  onClick={submitCreate}
                  loading={createRole.isPending}
                >
                  <IconCheck size={14} />
                </ActionIcon>
                <ActionIcon
                  color="gray"
                  variant="light"
                  onClick={() => setShowCreate(false)}
                >
                  <IconX size={14} />
                </ActionIcon>
              </Group>
              <Text size="xs" c="dimmed">
                After creating, go to the Permissions tab to assign what this
                role can do.
              </Text>
            </Stack>
          </Paper>
        )}

        {custom.length === 0 && !showCreate ? (
          <Text size="sm" c="dimmed">
            No custom roles yet.
          </Text>
        ) : (
          <Stack gap="xs">
            {custom.map((role) => (
              <RoleRow key={role.id} role={role} isAdmin={isAdmin} />
            ))}
          </Stack>
        )}
      </div>
    </Stack>
  );
}

// ─── Modal shell ─────────────────────────────────────────────────────────────

export function RolePermissionsModal({
  opened,
  onClose,
}: RolePermissionsModalProps) {
  const { data: auth } = useAuth();
  const isAdmin = auth?.isPlatformAdmin ?? false;
  const isMobile = useIsMobile();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen={isMobile}
      title={
        <Group gap="xs">
          <IconShieldCheck size={22} />
          <Title order={3}>Roles & Permissions</Title>
        </Group>
      }
      size="90vw"
      styles={{ body: { paddingTop: 0 } }}
    >
      <Tabs defaultValue="permissions" mt="sm">
        <Tabs.List mb="md">
          <Tabs.Tab
            value="permissions"
            leftSection={<IconShieldCheck size={14} />}
          >
            Permissions
          </Tabs.Tab>
          <Tabs.Tab value="roles" leftSection={<IconUsers size={14} />}>
            Roles
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="permissions">
          <PermissionsTab isAdmin={isAdmin} opened={opened} />
        </Tabs.Panel>

        <Tabs.Panel value="roles">
          <RolesTab isAdmin={isAdmin} opened={opened} />
        </Tabs.Panel>
      </Tabs>
    </Modal>
  );
}
